/**
 * Culling view-bounds math — the world-space rectangle a `SpaceRenderer`
 * treats as "possibly visible".
 *
 * Inputs are the QUANTIZED pan/zoom (see BoardCanvas: pan snapped to
 * `boundsStep` screen-px cells, zoom to `zoomStep`) so the returned object
 * stays referentially stable while a gesture moves within a cell. The
 * bounds must therefore cover the worst-case quantization slack:
 * - pan error: up to `boundsStep/2` SCREEN px in each axis;
 * - zoom error: up to `zoomStep/2` — the real zoom may be smaller than
 *   `quantZoom`, which makes the real visible WORLD rect larger than the
 *   one `quantZoom` projects.
 *
 * Both are screen-space errors, so the pad is specified in screen px and
 * converted at the LOWEST zoom the quantized value may stand for. (The old
 * form padded a fixed 300 *world* px, which at zoom ≲ ⅓ was smaller than
 * the slack — visible items got culled mid-cell at low zoom.)
 */

export interface ViewBounds {
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
}

/** Extra screen-px margin beyond the exact quantization slack — overscan so
 *  items at the very edge don't mount/unmount on every cell crossing. */
const OVERSCAN_SCREEN_PX = 200;

export function viewBoundsFor(
  quantPanX: number,
  quantPanY: number,
  quantZoom: number,
  width: number,
  height: number,
  zoomStep: number,
  boundsStep: number,
  /** Additional screen-px overscan — the gesture scheduler widens the pad
   *  with camera velocity so the cull set stays ahead of the camera between
   *  snapshot refresh ticks. */
  extraScreenPad = 0,
): ViewBounds | undefined {
  // Hidden (0×0) or not yet measured → no culling rather than cull-all.
  if (width === 0 || height === 0) return undefined;
  const pad = boundsStep / 2 + OVERSCAN_SCREEN_PX + extraScreenPad;
  // The real zoom lives in [quantZoom - zoomStep/2, quantZoom + zoomStep/2);
  // dividing screen extents by the LOW end over-includes, never under.
  const zLo = Math.max(0.01, quantZoom - zoomStep / 2);
  const zHi = quantZoom + zoomStep / 2;
  const x1s = 0 - quantPanX - pad;
  const y1s = 0 - quantPanY - pad;
  const x2s = width - quantPanX + pad;
  const y2s = height - quantPanY + pad;
  return {
    x1: Math.min(x1s / zLo, x1s / zHi),
    y1: Math.min(y1s / zLo, y1s / zHi),
    x2: Math.max(x2s / zLo, x2s / zHi),
    y2: Math.max(y2s / zLo, y2s / zHi),
  };
}
